import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, FileText, Printer, Share2 } from 'lucide-react';
import { MarkdownRenderer } from '@/legal/utils/markdownRenderer';
import { extractToc } from '@/legal/utils/tocExtractor';
import { LegalHeader } from './LegalHeader';
import { CeoIntro } from './CeoIntro';
import { LegalNote } from './LegalNote';
import { HeartbeatPulse } from './HeartbeatPulse';
import { TableOfContents } from './TableOfContents';
import { ReadingProgress } from './ReadingProgress';
import { DocumentSearch } from './DocumentSearch';
import { JurisdictionBadges } from './JurisdictionBadges';
import { MobileTocDrawer } from './MobileTocDrawer';

interface Props {
  title: string;
  content: string;
  version: string;
  effectiveDate: string;
  readingMinutes?: number;
  jurisdictions?: Array<'ru' | 'eu' | 'us'>;
  children?: ReactNode;
}

export function LegalDocument({ title, content, version, effectiveDate, readingMinutes, jurisdictions, children }: Props) {
  const { t, i18n } = useTranslation('legal');
  const toc = useMemo(() => extractToc(content), [content]);
  const [tocOpen, setTocOpen] = useState(false);
  // Print footer (`body::after { content: "Источник: " attr(data-page-url) }`
   // в print.css) читает атрибут `data-page-url` с <body>. Проставляем здесь —
   // на mount + cleanup при unmount.
  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    document.body.dataset.pageUrl = url;
    return () => {
      delete document.body.dataset.pageUrl;
    };
  }, []);

  const formattedDate = new Date(effectiveDate).toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // P0-3 (протокол №033): TLDR ПЕРЕД CeoIntro. Markdown файлы имеют структуру
  // `:::tldr ... :::\n\n---\n\n## 1. ...`. Сплитим content на tldrPart (до
  // первого `\n---\n`) и restPart (после), чтобы рендерить TLDR в hero,
  // а CeoIntro — после него.
  const [tldrPart, restPart] = useMemo<[string, string]>(() => {
    const idx = content.indexOf('\n---\n');
    if (idx === -1) return ['', content];
    return [content.slice(0, idx), content.slice(idx + 5)];
  }, [content]);

  // Wrapper div вместо React.Fragment — гарантирует, что sticky <LegalHeader>
  // имеет стабильный scrolling-context (viewport). Fragment рендерил header
  // и article как siblings #root без общего блочного предка — в некоторых
  // браузерных конфигурациях это давало sticky-уползание.
  return (
    <div className="min-h-screen">
      <LegalHeader title={title} onTocOpen={() => setTocOpen(true)} />
      <ReadingProgress />
      <div className="mx-auto max-w-4xl px-4 pb-8 pt-4 md:px-8">
        <header className="mb-6">
          <h1 className="font-display text-2xl font-semibold dark:font-bold text-text-primary md:text-3xl lg:text-4xl flex items-center gap-3 tracking-[-0.02em] break-words">
            <span>{title}</span>
            <HeartbeatPulse />
          </h1>
          {/* Мета-строка как pill-блок (bg-surface-subtle + rounded-md):
              визуально группирует версию + дату + время чтения + «Что изменилось».
              Без фона ссылка «Что изменилось» теряла контекст —
              непонятно к чему относится (пользователь, 2026-05-09).
              tabular-nums — моноширинные цифры для дат/версий. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-text-secondary tabular-nums">
            {/* Версия — иконка документа; ссылкой на changelog оборачиваем
                ТОЛЬКО номер версии, label «Версия» остаётся обычным текстом. */}
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" aria-hidden />
              {t('hero.versionLabel')}{' '}
              <a
                href="#changelog"
                className="text-text-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 rounded"
                aria-label={t('hero.whatChanged')}
              >
                {version}
              </a>
            </span>
            <span aria-hidden>·</span>
            {/* Действует с — иконка календаря */}
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden />
              {t('hero.effectiveFrom', { date: formattedDate })}
            </span>
            {/* Действия (print + share) — продолжение 1-й строки сразу после
                «Действует с ...». Без `ml-auto` чтобы прижать к контенту. */}
            <button
              type="button"
              onClick={() => window.print()}
              aria-label={t('actions.print')}
              title={t('actions.print')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
            >
              <Printer className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={async () => {
                const shareData = { title, url: window.location.href };
                try {
                  if (navigator.share) {
                    await navigator.share(shareData);
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(window.location.href);
                    // Минимальный visual feedback без отдельного toast-stack
                    // (полный toast — DT после v1.0.0).
                    // eslint-disable-next-line no-alert
                    alert(t('actions.shareCopied'));
                  }
                } catch {
                  // user cancelled — silent
                }
              }}
              aria-label={t('actions.share')}
              title={t('actions.share')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>

          <JurisdictionBadges jurisdictions={jurisdictions} />
        </header>

        {/* P0-3: TLDR (первый блок markdown-документа) рендерится ДО CeoIntro,
            чтобы пользователь сразу увидел «5 пунктов главного». */}
        {tldrPart && (
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <MarkdownRenderer content={tldrPart} />
          </div>
        )}

        <LegalNote>
          Если Вы нашли неточности или есть предложения по улучшению — напишите нам на e-mail:{' '}
          <a href="mailto:dpo@agentceo.ru" className="text-text-link hover:underline">
            dpo@agentceo.ru
          </a>
          .
        </LegalNote>

        <CeoIntro />

        <MobileTocDrawer items={toc} content={content} open={tocOpen} onOpenChange={setTocOpen} />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="space-y-4">
              <DocumentSearch content={content} />
              <TableOfContents items={toc} />
            </div>
          </aside>

          <main className="min-w-0">
            {/* Pill-label «ПОЛНЫЙ ТЕКСТ» — симметричен «КРАТКОЕ СОДЕРЖАНИЕ»
                из TldrPill. Справа — длительность чтения полного документа. */}
            <div className="mb-8 mt-2 flex flex-wrap items-center gap-3">
              <div
                className="inline-block rounded-full bg-action-primary-bg px-3 py-1 text-xs font-medium uppercase tracking-wider text-text-on-brand"
                aria-hidden
              >
                Полный текст
              </div>
              {readingMinutes !== undefined && (
                <span className="inline-flex items-center gap-1.5 text-sm text-text-secondary tabular-nums">
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  {t('hero.readingTime', { minutes: readingMinutes })}
                </span>
              )}
            </div>
            <article className="prose prose-neutral dark:prose-invert max-w-none prose-strong:font-semibold prose-p:font-light prose-li:font-light prose-td:font-light [&_h2:first-of-type]:mt-0">
              <MarkdownRenderer content={restPart} />
            </article>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
